import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERFIS_CONTATO, type Contato } from '@eleg360/types';
import { contatosApi } from '@/lib/api/contatos';
import { visitasApi } from '@/lib/api/visitas';
import { useAuthStore } from '@/lib/stores/authStore';
import { BASE_LEGAL_SEM_CONSENTIMENTO, motivoBloqueio } from '@/lib/lgpd';
import { toast } from '@/lib/stores/toastStore';

interface Props {
  visitaId: string;
  /** Território da visita: é o recorte padrão da busca e o bairro do contato rápido. */
  bairro: string;
  municipio: string;
  liderancaId?: string;
  liderancaNome?: string;
}

/** Quantos contatos a lista de sugestão mostra de uma vez (proto:1290, `.slice(0,40)`). */
const LIMITE_SUGESTOES = 40;

const primeiroNome = (nome: string) => nome.trim().split(/\s+/)[0] || nome;
const doisPrimeirosNomes = (nome: string) => nome.trim().split(/\s+/).slice(0, 2).join(' ');

const erroDe = (err: any, padrao: string) => {
  const msg = err?.response?.data?.message;
  return Array.isArray(msg) ? msg.join(' · ') : msg || padrao;
};

/**
 * `partPicker` do protótipo (proto:1290): quem esteve na visita.
 *
 * Duas regras de busca que o protótipo distingue e que não são detalhe de
 * conveniência: sem texto digitado, sugere só quem mora no bairro da visita
 * (é o caso comum — a reunião é ali); com texto, procura em qualquer bairro,
 * porque quem organiza a rua costuma morar na de trás.
 *
 * Os dois filtros vão para o servidor. Trazer a base inteira e peneirar na
 * tela daria a lista certa só enquanto ela coubesse numa página.
 */
export function SeletorParticipantes({
  visitaId,
  bairro,
  municipio,
  liderancaId,
  liderancaNome,
}: Props) {
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();

  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [aviso, setAviso] = useState('');

  // Uma requisição por palavra digitada, e não por tecla.
  useEffect(() => {
    const t = setTimeout(() => setBuscaAplicada(busca.trim()), 200);
    return () => clearTimeout(t);
  }, [busca]);

  const chaveParticipantes = ['visita-participantes', visitaId];

  const { data: participantes = [] } = useQuery({
    queryKey: chaveParticipantes,
    queryFn: () => visitasApi.listarParticipantes(visitaId, token),
    enabled: !!token && !!visitaId,
  });

  const idsPresentes = useMemo(
    () => new Set(participantes.map((p) => p.contato_id)),
    [participantes],
  );

  const { data: sugestoes } = useQuery({
    queryKey: ['contatos', 'participantes', buscaAplicada || `bairro:${bairro}`],
    queryFn: () =>
      contatosApi.list(
        buscaAplicada
          ? { search: buscaAplicada, limit: LIMITE_SUGESTOES }
          : { bairro, limit: LIMITE_SUGESTOES },
        token,
      ),
    enabled: !!token,
  });

  /**
   * A rede da liderança: os contatos com `lideranca_id` apontando para ela.
   * No protótipo era uma lista fixa dentro do objeto da liderança; aqui é o
   * vínculo que já existe no cadastro.
   */
  const { data: rede } = useQuery({
    queryKey: ['contatos', 'rede', liderancaId],
    queryFn: () => contatosApi.list({ lideranca_id: liderancaId, limit: 500 }, token),
    enabled: !!token && !!liderancaId,
  });

  const adicionar = useMutation({
    mutationFn: (ids: string[]) => visitasApi.adicionarParticipantes(visitaId, ids, token),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: chaveParticipantes }),
    onError: (err: any) => toast(erroDe(err, 'Não foi possível adicionar o participante.')),
  });

  const remover = useMutation({
    mutationFn: (contatoId: string) =>
      visitasApi.removerParticipante(visitaId, contatoId, token),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: chaveParticipantes }),
    onError: (err: any) => toast(erroDe(err, 'Não foi possível remover o participante.')),
  });

  const listaSugerida = (sugestoes?.items ?? []).filter((c) => !idsPresentes.has(c.id));

  const adicionarRede = () => {
    const daRede = (rede?.items ?? []).map((c) => c.id);
    // Dedup antes de enviar: o índice único do banco recusaria o lote inteiro
    // por causa de um contato que já estava lá.
    const novos = daRede.filter((id) => !idsPresentes.has(id));
    if (daRede.length === 0) {
      setAviso(`Nenhum contato está vinculado a ${liderancaNome} no cadastro.`);
      return;
    }
    if (novos.length === 0) {
      setAviso(`Os ${daRede.length} contatos da rede de ${liderancaNome} já estão na visita.`);
      return;
    }
    setAviso('');
    adicionar.mutate(novos);
  };

  return (
    <div className="card flat" style={{ marginTop: 16 }}>
      <h3>Participantes ({participantes.length})</h3>

      <div className="filters" data-testid="participantes-escolhidos" style={{ margin: '6px 0' }}>
        {participantes.map((p) => {
          const bloqueio = motivoBloqueio({ base_legal: p.contato_base_legal });
          return (
            <span key={p.id} className="chip acc" title={bloqueio ?? undefined}>
              {doisPrimeirosNomes(p.contato_nome)}
              {bloqueio && (
                <span style={{ marginLeft: 4 }} aria-label="sem autorização de envio">
                  ⚠
                </span>
              )}{' '}
              <button
                type="button"
                className="btn sm"
                aria-label={`Remover ${p.contato_nome}`}
                disabled={remover.isPending}
                onClick={() => remover.mutate(p.contato_id)}
                style={{ padding: '0 4px', border: 'none', background: 'none' }}
              >
                ×
              </button>
            </span>
          );
        })}
        {participantes.length === 0 && (
          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>ninguém ainda</span>
        )}
      </div>

      <div className="filters">
        <input
          className="in"
          style={{ width: 220, padding: '5px 9px' }}
          placeholder="Buscar contato (qualquer bairro)…"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        {liderancaId && liderancaNome && (
          <button type="button" className="btn sm" onClick={adicionarRede}>
            + rede de {primeiroNome(liderancaNome)}
          </button>
        )}
      </div>

      {aviso && (
        <div className="note" style={{ fontSize: 12, marginTop: 6 }}>
          {aviso}
        </div>
      )}

      <div
        data-testid="participantes-sugestoes"
        style={{
          maxHeight: 160,
          overflow: 'auto',
          border: '1px solid var(--line)',
          borderRadius: 8,
          marginTop: 6,
        }}
      >
        {listaSugerida.map((c: Contato) => (
          <div
            key={c.id}
            role="button"
            tabIndex={0}
            onClick={() => adicionar.mutate([c.id])}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') adicionar.mutate([c.id]);
            }}
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              padding: '5px 10px',
              borderBottom: '1px solid var(--line)',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            <span>+</span>
            <span>{c.nome}</span>
            <span style={{ fontSize: 11, color: 'var(--ink3)' }}>{c.bairro || '—'}</span>
            {(c.perfil || [])
              .filter((p) => p !== 'Cidadão(ã)')
              .slice(0, 1)
              .map((p) => (
                <span key={p} className="chip acc" style={{ fontSize: 10 }}>
                  {p}
                </span>
              ))}
          </div>
        ))}
        {listaSugerida.length === 0 && (
          <div className="empty" style={{ fontSize: 12 }}>
            nenhum contato; cadastre abaixo
          </div>
        )}
      </div>

      <ContatoRapido
        bairro={bairro}
        municipio={municipio}
        aoCriar={(id) => adicionar.mutate([id])}
      />
    </div>
  );
}

/**
 * "Pessoa sem cadastro? Adicionar contato rápido" (proto:1290).
 *
 * Nasce sem base legal de propósito: quem foi anotado numa lista de presença
 * não autorizou receber mensagem. O aviso abaixo do botão é o do protótipo,
 * palavra por palavra, porque é ele que transforma a lacuna em tarefa
 * ("registre o opt-in depois na ficha") em vez de deixá-la invisível.
 */
function ContatoRapido({
  bairro,
  municipio,
  aoCriar,
}: {
  bairro: string;
  municipio: string;
  aoCriar: (contatoId: string) => void;
}) {
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [perfil, setPerfil] = useState<string>(PERFIS_CONTATO[0]);

  const criar = useMutation({
    mutationFn: () =>
      contatosApi.create(
        {
          nome: nome.trim(),
          telefone_e164: telefone.trim() || undefined,
          bairro,
          municipio,
          perfil: [perfil],
          tags: [],
          base_legal: BASE_LEGAL_SEM_CONSENTIMENTO,
          origem: 'agenda interna',
          status: 'ativo',
        } as any,
        token,
      ),
    onSuccess: (c) => {
      queryClient.invalidateQueries({ queryKey: ['contatos'] });
      setNome('');
      setTelefone('');
      aoCriar(c.id);
      toast('Contato criado e adicionado.');
    },
    onError: (err: any) => toast(erroDe(err, 'Não foi possível criar o contato.')),
  });

  return (
    <details style={{ marginTop: 8 }}>
      <summary style={{ fontSize: 13, color: 'var(--accent)', cursor: 'pointer' }}>
        Pessoa sem cadastro? Adicionar contato rápido
      </summary>

      <div className="grid g3" style={{ marginTop: 8 }}>
        <div className="field">
          <label htmlFor="qc-nome">Nome</label>
          <input
            id="qc-nome"
            className="in"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="qc-tel">Telefone</label>
          <input
            id="qc-tel"
            className="in"
            placeholder="(85)"
            value={telefone}
            onChange={(e) => setTelefone(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="qc-perfil">Perfil</label>
          <select
            id="qc-perfil"
            className="in"
            value={perfil}
            onChange={(e) => setPerfil(e.target.value)}
          >
            {PERFIS_CONTATO.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
      </div>

      <button
        type="button"
        className="btn sm pri"
        style={{ marginTop: 6 }}
        disabled={criar.isPending}
        onClick={() => {
          if (!nome.trim()) {
            toast('Informe o nome');
            return;
          }
          criar.mutate();
        }}
      >
        Criar e adicionar como participante
      </button>

      <p style={{ fontSize: 11, color: 'var(--ink3)', margin: '4px 0 0' }}>
        Nasce sem base legal, no bairro da visita; registre o opt-in depois na ficha.
      </p>
    </details>
  );
}
